import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../lib/i18n';
import { blogDraftReviewHtml, blogDraftReviewOptions, blogDraftShowsPost, blogDraftTitle } from '../../../lib/blog-draft-preview';
import { useBlogDraftPreview } from '../../../lib/blog-draft-preview-api';
import { useBlogDraftPreviewEnabled } from '../../../lib/blog-draft-preview-flag';
import { useBlogReviewFlow } from '../../../lib/blog-review-store';
import { draftHeadline, draftReviewHtml, draftReviewName, draftReviewKey, draftReviewMessage, draftReviewPageUrl, type DraftPreviewCardData } from '../../../lib/draft-preview';
import { useDraftPreview } from '../../../lib/draft-preview-api';
import { closeDraftReview, openDraftReview, useDraftReviewFlow, type DraftReviewFlow, type DraftReviewSendResult } from '../../../lib/draft-review-store';
import { emailReviewDocument, reviewNonce } from '../../../lib/email-review';
import { useEmailReviewFlow } from '../../../lib/email-review-store';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useIsPhone } from '../../../lib/use-is-phone';
import { useLoadBi } from '../../../lib/use-load-bi';
import { Button, Typography } from '../ui';
import { BlogDraftEditor } from './BlogDraftEditor';
import { SiteReviewFrame, type ReviewDocument } from './SiteReviewFrame';
import styles from './SiteEditHost.module.css';
import own from './EmailReviewHost.module.css';

function draftCopy(card: Pick<DraftPreviewCardData, 'kind'>): 'emailReview' | 'blogDraft' {
  return card.kind === 'EMAIL' ? 'emailReview' : 'blogDraft';
}

export function DraftReviewHost() {
  const flow = useDraftReviewFlow();
  const emailFlow = useEmailReviewFlow();
  const blogFlow = useBlogReviewFlow();
  useEffect(() => {
    if (emailFlow || blogFlow) closeDraftReview();
  }, [emailFlow, blogFlow]);
  if (!flow || flow.beside || typeof document === 'undefined') return null;
  return <DraftReviewSheet flow={flow} />;
}

export function DraftReviewPane({ flow }: { flow: DraftReviewFlow }) {
  return <DraftReviewOrEditor flow={flow} layout="pane" />;
}

function DraftReviewOrEditor({ flow, layout }: { flow: DraftReviewFlow; layout: 'pane' | 'sheet' }) {
  const blogEditor = useBlogDraftPreviewEnabled();
  if (flow.card.kind !== 'BLOG_POST') return <DraftReview flow={flow} layout={layout} />;
  if (blogEditor === undefined) return null;
  return blogEditor ? <BlogDraftEditor flow={flow} layout={layout} /> : <DraftReview flow={flow} layout={layout} />;
}

function DraftReviewSheet({ flow }: { flow: DraftReviewFlow }) {
  const { t } = useTranslation();
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return createPortal(
    <div
      className={styles.sheet}
      role="dialog"
      aria-modal="true"
      aria-label={draftHeadline(flow.card) || t(`${draftCopy(flow.card)}.title`)}
      data-testid="draftReviewSheet"
    >
      <DraftReviewOrEditor flow={flow} layout="sheet" />
    </div>,
    document.body,
  );
}

function DraftReview({ flow, layout }: { flow: DraftReviewFlow; layout: 'pane' | 'sheet' }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { card, spaceSlug, conversationId } = flow;
  const copy = draftCopy(card);
  const [refused, setRefused] = useState<Exclude<DraftReviewSendResult, { status: 'sent' }> | null>(null);
  const wix = card.kind === 'BLOG_POST' ? (flow.wixPreview ?? null) : null;
  const phone = useIsPhone();
  const wixRead = useBlogDraftPreview(
    { workspaceId: card.workspaceId, metaSiteId: wix?.siteId ?? '', draftPostId: wix?.draftPostId ?? '' },
    phone ? 'mobile' : 'desktop',
    wix !== null,
    flow.revision,
  );
  const wixShowsPost = useMemo(() => !wixRead.data || blogDraftShowsPost(wixRead.data), [wixRead.data]);
  const onWix = wix !== null && !wixRead.isError && wixShowsPost;
  const preview = useDraftPreview(onWix ? null : { workspaceId: card.workspaceId, taskId: card.taskId, draftId: card.draftId, kind: card.kind }, flow.revision);
  const read = onWix ? wixRead : preview;
  useLoadBi('draft-review', { isLoading: read.isLoading, isError: read.isError });
  const pageUrl = draftReviewPageUrl(card.draftId);
  const headline = onWix && wixRead.data ? blogDraftTitle(wixRead.data, card.title) : draftHeadline(card, preview.data);
  const label = headline || t(`${copy}.title`);
  const loading = t(`${copy}.loading`);
  const unavailable = t(`${copy}.unavailable`);
  const reviewDocument = useMemo<ReviewDocument | null>(() => {
    const html = onWix ? (wixRead.data ? blogDraftReviewHtml(wixRead.data, headline) : null) : preview.data ? draftReviewHtml(card, preview.data) : null;
    const options = onWix && wixRead.data ? blogDraftReviewOptions(wixRead.data) : {};
    return html === null ? null : { srcDoc: emailReviewDocument(html, pageUrl, reviewNonce(), options), label, loading, unavailable };
  }, [onWix, wixRead.data, preview.data, card, headline, pageUrl, label, loading, unavailable]);

  useEffect(() => {
    if (reviewDocument || layout !== 'sheet') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDraftReview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reviewDocument, layout]);

  const finish = (message: string): string | null | false => {
    const result = flow.sendComments(message);
    if (result.status !== 'sent') {
      setRefused(result);
      return false;
    }
    closeDraftReview();
    return conversationId;
  };

  if (!reviewDocument) {
    return (
      <div className={layout === 'pane' ? own.paneStatus : styles.body} role="status" data-testid="draftReviewStatus">
        <Typography variant="p2" color="primary">
          {read.isError ? unavailable : loading}
        </Typography>
        <Button variant="outlined" onClick={closeDraftReview}>
          {t('siteReview.close')}
        </Button>
      </div>
    );
  }
  const openNewer = (newer: Extract<DraftReviewSendResult, { status: 'newer' }>) => {
    logPageClickBi({ page_name: 'chat', button_name: 'draft_review_open_newer', ...(conversationId ? { conversation_id: conversationId } : {}) });
    setRefused(null);
    openDraftReview({ ...flow, card: newer.card, sendComments: newer.sendComments, wixPreview: null, revision: undefined });
    void read.refetch();
  };

  return (
    <div className={own.review}>
      {refused && (
        <div className={own.notice} role="status" data-testid="draftReviewRefused">
          <Typography variant="p3" color="primary">
            {t(`${copy}.refused.${refused.status}`)}
          </Typography>
          {refused.status === 'newer' && (
            <Button size="small" variant="outlined" onClick={() => openNewer(refused)} data-testid="draftReviewOpenNewer">
              {t(`${copy}.refused.openNewer`)}
            </Button>
          )}
        </div>
      )}
      <SiteReviewFrame
        previewUrl={pageUrl}
        reviewKey={draftReviewKey(card.draftId, conversationId)}
        layout={layout}
        conversationId={conversationId}
        reviewDocument={reviewDocument}
        buildMessage={(comments) => draftReviewMessage({ kind: card.kind, taskId: card.taskId, draftId: card.draftId, spaceSlug, name: draftReviewName(card, preview.data) }, comments)}
        onReload={() => void read.refetch()}
        onFinish={finish}
        onClose={closeDraftReview}
      />
    </div>
  );
}
